import React, { useCallback, useState, useRef, useEffect } from "react";
import { useNavigate } from "react-router-dom";
import { useDynamicConfig, useStatsigClient } from '@statsig/react-bindings';
import { FiCopy, FiExternalLink } from "react-icons/fi";
import "./LinkCard.scss";
import Tooltip from "../Tooltip";
import { copyToClipboard } from "../../utils/copyToClipboard";
import { AI_ASSISTANT_PENDO_EVENTS, trackAssistantEvent } from "../../utils/assistantAnalytics";

import LineIcon from "@/assets/icons/line.svg";
import { AI_ASSISTANT_CONSTANTS } from "../../constants/aiAssistantConstants";

const LinkCard = ({ title, description, link }) => {
    const navigate = useNavigate();
    const [copyLabel, setCopyLabel] = useState("Copy link");
    const copyTimerRef = useRef(null);
    useEffect(() => () => { if (copyTimerRef.current) clearTimeout(copyTimerRef.current); }, []);
    const { client } = useStatsigClient();
    const config = useDynamicConfig(AI_ASSISTANT_CONSTANTS.DYNAMIC_CONFIGS.AI_ASSISTANT_CONFIGS);

    const handleInternalNavigation = useCallback((e) => {
        if (e) e.stopPropagation();
        if (!link) return;
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.LINK_OPENED, {
            linkType: "link_card_internal",
        });
        try {
            if (link.startsWith("http")) {
                const url = new URL(link);
                const isSameOrigin = url.origin === window.location.origin;
                if (isSameOrigin) {
                    sessionStorage.setItem("params", url.search);
                    navigate(url.pathname + url.search);
                } else {
                    window.location.href = link;
                }
            } else {
                navigate(link);
            }
        } catch (error) {
            navigate(link);
        }
    }, [link, navigate]);

    const handleExternalClick = (e) => {
        e.stopPropagation();
        trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.LINK_OPENED, {
            linkType: "link_card",
        });
        window.open(link, "_blank", "noopener,noreferrer");
    };

    const handleCopyLink = useCallback(async (e) => {
        e.stopPropagation();
        if (!link) return;
        if (copyTimerRef.current) clearTimeout(copyTimerRef.current);
        const ok = await copyToClipboard(link);
        if (ok) {
            trackAssistantEvent(AI_ASSISTANT_PENDO_EVENTS.LINK_COPIED, {
                linkType: "link_card",
            });
            setCopyLabel("Copied");
            copyTimerRef.current = setTimeout(() => setCopyLabel("Copy link"), 2000);
        }
    }, [link]);

    return (
        <div className="link-card" onClick={handleExternalClick}>
            <div className="left-section">
                <div className="icon-wrapper">
                    <img src={LineIcon} alt="Icon" className="icon" />
                </div>
                <div className="text-content">
                    <div className="title">{title}</div>
                    <div
                        className="description"
                        onClick={handleExternalClick}
                    >
                        {description}
                    </div>
                </div>
            </div>
            <div className="right-section">
                <Tooltip content={copyLabel} placement="top" arrow={true} variant="light">
                    <button
                        type="button"
                        className="link-card__action-btn"
                        onClick={handleCopyLink}
                        aria-label={copyLabel}
                    >
                        <FiCopy aria-hidden />
                    </button>
                </Tooltip>
                <Tooltip content="Open in new tab" placement="top" arrow={true} variant="light">
                    <button
                        type="button"
                        className="link-card__action-btn"
                        onClick={handleExternalClick}
                        aria-label="Open in new tab"
                    >
                        <FiExternalLink aria-hidden />
                    </button>
                </Tooltip>
            </div>
        </div>
    );
};

export default LinkCard;
